import { BasecampRole } from "@utils/types";
import { useState } from "react";

import { Button, Dialog } from "@fronterahealth/frontera-ui-components";

import {
  EhrIntegrationTypeEnum,
  type GetOrganizationsOutput,
  useBasecampOrganizationsUpdateCreate,
} from "@api/rest/types-and-hooks";
import { InfoTooltip } from "@components/InfoTooltip";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useBasecampRoles } from "@hooks/useBasecampRoles";
import { EhrIntegrationTypeField } from "@pages/Organizations/EhrIntegrationTypeField";
import { GoalGenerationRulesSection } from "@pages/Organizations/GoalGenerationRulesSection";
import { VerticalOrgIdSection } from "@pages/Organizations/VerticalOrgIdSection";
import {
  EhrSyncMode,
  REVIEW_SETTINGS,
  REVIEW_SETTING_DEFAULTS,
  type ReviewSettingKey,
  reviewSettingsErrorMessage,
} from "@pages/Organizations/constants";
import {
  EHR_INTEGRATION_TYPE_CONFIRM_TEXT,
  EHR_INTEGRATION_TYPE_LABELS,
} from "@pages/Organizations/ehrIntegrationType";
import { EHR_SYNC_COPY, getEhrSyncMode, takesVerticalOrgId } from "@pages/Organizations/ehrSync";

const NO_PERMISSION_TOOLTIP = "Only Basecamp admins and enablement admins can change the EHR sign-in method.";

interface OrganizationSettingsTabProps {
  orgId: string;
  org: GetOrganizationsOutput | undefined;
  onSaved: () => Promise<unknown> | void;
}

export const OrganizationSettingsTab: React.FC<OrganizationSettingsTabProps> = ({ orgId, org, onSaved }) => {
  // Derived-state pattern: the saved value comes from the org query; `pendingValue`
  // holds the user's in-flight edit (null = untouched). This avoids syncing via an
  // effect when the org loads/refetches.
  const savedValue = org?.require_all_qa_resolved ?? false;
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const requireAllQaResolved = pendingValue ?? savedValue;
  const { mutateAsync: updateOrganization, isPending } = useBasecampOrganizationsUpdateCreate();

  const isDirty = !!org && pendingValue !== null && pendingValue !== savedValue;

  const savedAdminAnalyticsValue = org?.require_admin_analytics ?? false;
  const [pendingAdminAnalyticsValue, setPendingAdminAnalyticsValue] = useState<boolean | null>(null);
  const requireAdminAnalytics = pendingAdminAnalyticsValue ?? savedAdminAnalyticsValue;
  const { mutateAsync: updateOrganizationAdminAnalytics, isPending: isAdminAnalyticsPending } =
    useBasecampOrganizationsUpdateCreate();

  const isAdminAnalyticsDirty =
    !!org && pendingAdminAnalyticsValue !== null && pendingAdminAnalyticsValue !== savedAdminAnalyticsValue;

  const [pendingReviewSettings, setPendingReviewSettings] = useState<Partial<Record<ReviewSettingKey, boolean>>>({});
  const { mutateAsync: updateOrganizationReviewSettings, isPending: isReviewSettingsPending } =
    useBasecampOrganizationsUpdateCreate();

  const isReviewSettingsDirty =
    !!org &&
    REVIEW_SETTINGS.some(
      ({ key }) => pendingReviewSettings[key] !== undefined && pendingReviewSettings[key] !== org[key],
    );

  // Passage is read-only and always on; an older API without `organization_type` stays editable.
  const ehrSyncMode = org && !org.organization_type ? EhrSyncMode.EDITABLE : getEhrSyncMode(org?.organization_type);
  const isEhrSyncEditable = ehrSyncMode === EhrSyncMode.EDITABLE;
  const savedEhrSyncEnabledValue = !org ? false : isEhrSyncEditable ? org.ehr_sync_enabled : org.ehr_sync_cleared;
  const [pendingEhrSyncEnabledValue, setPendingEhrSyncEnabledValue] = useState<boolean | null>(null);
  const ehrSyncEnabled = isEhrSyncEditable
    ? (pendingEhrSyncEnabledValue ?? savedEhrSyncEnabledValue)
    : savedEhrSyncEnabledValue;
  const { mutateAsync: updateOrganizationEhrSync, isPending: isEhrSyncPending } =
    useBasecampOrganizationsUpdateCreate();

  const isEhrSyncDirty =
    isEhrSyncEditable && pendingEhrSyncEnabledValue !== null && pendingEhrSyncEnabledValue !== savedEhrSyncEnabledValue;

  const savedIntegrationType = org?.ehr_integration_type ?? EhrIntegrationTypeEnum.STANDARD;
  const [pendingIntegrationType, setPendingIntegrationType] = useState<EhrIntegrationTypeEnum | null>(null);
  const integrationType = pendingIntegrationType ?? savedIntegrationType;
  const { mutateAsync: updateOrganizationIntegrationType, isPending: isIntegrationTypePending } =
    useBasecampOrganizationsUpdateCreate();
  const [isConfirmTypeOpen, setConfirmTypeOpen] = useState(false);
  // Spans a whole Save, including the refetches between requests, when no
  // mutation is pending — without it Save is briefly clickable mid-flow.
  const [isSaving, setIsSaving] = useState(false);
  // Enterprise admins can view this page, but the update endpoint only allows
  // admin / enablement admin — lock the field instead of letting them hit a 403.
  const roles = useBasecampRoles();
  const canEditIntegrationType = roles.includes(BasecampRole.ADMIN) || roles.includes(BasecampRole.ENABLEMENT_ADMIN);

  const isIntegrationTypeDirty =
    !!org && pendingIntegrationType !== null && pendingIntegrationType !== savedIntegrationType;

  const handleSave = async (): Promise<boolean> => {
    const response = await updateOrganization({
      data: { organization_id: orgId, require_all_qa_resolved: requireAllQaResolved },
    });

    if (response.status !== 200) {
      notifyError(
        (response.data && "detail" in response.data ? response.data.detail : null) ||
          "Failed to update organization settings",
      );
      return false;
    }

    notifySuccess("Organization settings updated");
    // Refetch first so the derived `savedValue` already reflects the new value,
    // then drop the local edit — otherwise the checkbox briefly snaps back to the
    // stale value between clearing and the refetch landing.
    await onSaved();
    setPendingValue(null);
    return true;
  };

  const handleSaveAdminAnalytics = async (): Promise<boolean> => {
    const response = await updateOrganizationAdminAnalytics({
      data: { organization_id: orgId, require_admin_analytics: requireAdminAnalytics },
    });

    if (response.status !== 200) {
      notifyError(
        (response.data && "detail" in response.data ? response.data.detail : null) ||
          "Failed to update organization settings",
      );
      return false;
    }

    notifySuccess("Organization settings updated");
    await onSaved();
    setPendingAdminAnalyticsValue(null);
    return true;
  };

  const handleSaveReviewSettings = async (): Promise<boolean> => {
    // Drop keys toggled back to the saved value so they can't overwrite a concurrent change.
    const changed = REVIEW_SETTINGS.filter(
      ({ key }) => pendingReviewSettings[key] !== undefined && pendingReviewSettings[key] !== org?.[key],
    ).map(({ key }) => [key, pendingReviewSettings[key]]);
    const response = await updateOrganizationReviewSettings({
      data: { organization_id: orgId, ...Object.fromEntries(changed) },
    });

    if (response.status !== 200) {
      notifyError(reviewSettingsErrorMessage(response.data) || "Failed to update organization settings");
      return false;
    }

    notifySuccess("Organization settings updated");
    await onSaved();
    setPendingReviewSettings({});
    return true;
  };

  const handleSaveEhrSync = async (): Promise<boolean> => {
    const response = await updateOrganizationEhrSync({
      data: { organization_id: orgId, ehr_sync_enabled: ehrSyncEnabled },
    });

    if (response.status !== 200) {
      notifyError(
        (response.data && "detail" in response.data ? response.data.detail : null) ||
          "Failed to update organization settings",
      );
      return false;
    }

    notifySuccess("Organization settings updated");
    await onSaved();
    setPendingEhrSyncEnabledValue(null);
    return true;
  };

  const handleSaveIntegrationType = async () => {
    let response: Awaited<ReturnType<typeof updateOrganizationIntegrationType>>;
    try {
      response = await updateOrganizationIntegrationType({
        data: { organization_id: orgId, ehr_integration_type: integrationType },
      });
    } catch {
      // The switch tears down live EHR sessions server-side and can outlast the
      // proxy timeout, returning a non-JSON 502/504 that makes the fetcher throw.
      // The type may already be committed, so refetch to show the real value —
      // the pending edit stays dirty only if the switch didn't land.
      notifyError("Couldn't confirm the EHR sign-in method update. Showing the current saved value.");
      try {
        await onSaved();
      } catch {
        // The toast above already reports the failure; a failed refetch just
        // leaves the last loaded value on screen.
      } finally {
        // Drop the edit so the field shows what is actually saved, as the
        // toast says — not the choice that may not have landed.
        setPendingIntegrationType(null);
      }
      return;
    }

    if (response.status !== 200) {
      notifyError(
        (response.data && "detail" in response.data ? response.data.detail : null) ||
          "Failed to update organization settings",
      );
      return;
    }

    notifySuccess("EHR sign-in method updated");
    try {
      await onSaved();
    } catch {
      // The switch landed; a failed refetch only leaves stale values on screen.
    } finally {
      // Always drop the edit once the switch is saved, so the field isn't
      // left showing unsaved changes after the success toast.
      setPendingIntegrationType(null);
    }
  };

  const areTogglesDirty = isDirty || isAdminAnalyticsDirty || isEhrSyncDirty || isReviewSettingsDirty;
  const isAnySavePending =
    isSaving ||
    isPending ||
    isAdminAnalyticsPending ||
    isEhrSyncPending ||
    isReviewSettingsPending ||
    isIntegrationTypePending;

  // The toggles hit separate endpoints, so only the dirty one(s) fire.
  // Resolves false as soon as one fails — a non-200 (already toasted) or a
  // throw (non-JSON gateway error, network) — and never rejects.
  const saveDirtyToggles = async (): Promise<boolean> => {
    try {
      if (isDirty && !(await handleSave())) return false;
      if (isAdminAnalyticsDirty && !(await handleSaveAdminAnalytics())) return false;
      if (isEhrSyncDirty && !(await handleSaveEhrSync())) return false;
      if (isReviewSettingsDirty && !(await handleSaveReviewSettings())) return false;
      return true;
    } catch {
      // A throw can also come from the refetch after a save that landed, so
      // don't claim the save failed.
      notifyError("Couldn't confirm the organization settings update. Reload to see the saved values.");
      return false;
    }
  };

  // One Save covers everything. Type changes tear down live EHR sessions, so
  // they go through a confirm first — and nothing is saved until it's
  // answered, so Cancel never leaves the toggles half-saved behind one click.
  const handleSaveAll = async () => {
    if (isIntegrationTypeDirty) {
      setConfirmTypeOpen(true);
      return;
    }
    setIsSaving(true);
    try {
      await saveDirtyToggles();
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmTypeChange = async () => {
    setConfirmTypeOpen(false);
    setIsSaving(true);
    try {
      // The switch ends every active EHR session, so it only goes ahead once
      // the rest of the same Save has landed. On a failure the type stays
      // unsaved, ready to retry.
      if (!(await saveDirtyToggles())) {
        notifyError("The EHR sign-in method was not changed.");
        return;
      }
      await handleSaveIntegrationType();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-8 lg:px-16 flex flex-col gap-6 pb-12">
      <section className="flex flex-col gap-4 rounded-xl border border-border-medium bg-bg-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-10">
          <div className="flex items-center gap-2">
            <input
              id="require-qa-resolution"
              type="checkbox"
              checked={requireAllQaResolved}
              onChange={(e) => setPendingValue(e.target.checked)}
              className="h-4 w-4 rounded border-border-medium text-rio-600"
            />
            <label htmlFor="require-qa-resolution" className="cursor-pointer text-sm font-medium text-text-primary">
              Require QA resolution
            </label>
            {/* Tooltip must sit OUTSIDE the <label> — label event forwarding
                breaks the hover trigger (see GoalGenerationRulesSection). */}
            <InfoTooltip
              label="About Require QA resolution"
              text="When enabled, users in this organization cannot advance past review-and-sign until every active QA suggestion on the report is resolved. Applies to all templates used by this organization."
              iconClassName="h-3.5 w-3.5 text-text-tertiary"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="require-admin-analytics"
              type="checkbox"
              checked={requireAdminAnalytics}
              onChange={(e) => setPendingAdminAnalyticsValue(e.target.checked)}
              className="h-4 w-4 rounded border-border-medium text-rio-600"
            />
            <label htmlFor="require-admin-analytics" className="cursor-pointer text-sm font-medium text-text-primary">
              Require admin analytics
            </label>
            {/* Tooltip must sit OUTSIDE the <label> — label event forwarding
                breaks the hover trigger (see GoalGenerationRulesSection). */}
            <InfoTooltip
              label="About Require admin analytics"
              text="When enabled, admin dashboard analytics metrics are computed and stored for this organization. Cannot be enabled for test organizations."
              iconClassName="h-3.5 w-3.5 text-text-tertiary"
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {/* Passage stays focusable (aria-disabled, not disabled) so
                  screen readers still reach its "always on" description. */}
              <input
                id="ehr-sync-enabled"
                type="checkbox"
                checked={ehrSyncEnabled}
                disabled={!org}
                aria-disabled={ehrSyncMode === EhrSyncMode.ALWAYS_ON || undefined}
                aria-describedby={org && !isEhrSyncEditable ? "ehr-sync-enabled-help" : undefined}
                onChange={(e) => {
                  if (isEhrSyncEditable) setPendingEhrSyncEnabledValue(e.target.checked);
                }}
                className="h-4 w-4 rounded border-border-medium text-rio-600 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              />
              <label
                htmlFor="ehr-sync-enabled"
                className={`text-sm font-medium ${isEhrSyncEditable ? "cursor-pointer text-text-primary" : "cursor-not-allowed text-text-tertiary"}`}
              >
                EHR sync enabled
              </label>
              {/* Tooltip must sit OUTSIDE the <label> — label event forwarding
                  breaks the hover trigger (see GoalGenerationRulesSection). */}
              <InfoTooltip
                text={EHR_SYNC_COPY.tooltip}
                label={EHR_SYNC_COPY.tooltipLabel}
                iconClassName="h-3.5 w-3.5 text-text-tertiary"
              />
            </div>
            {org && !isEhrSyncEditable && (
              <p id="ehr-sync-enabled-help" className="text-xs text-text-secondary">
                {EHR_SYNC_COPY.help[ehrSyncMode]}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 sm:ml-auto">
            <span className="text-xs text-text-tertiary">
              {areTogglesDirty || isIntegrationTypeDirty ? "Unsaved changes" : "All changes saved"}
            </span>
            <Button
              variant="primary"
              buttonStyle="filled"
              size="sm"
              text={isAnySavePending ? "Saving..." : "Save"}
              disabled={(!areTogglesDirty && !isIntegrationTypeDirty) || isAnySavePending}
              onClick={handleSaveAll}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-10">
          {REVIEW_SETTINGS.map(({ key, label, tooltip }) => (
            <div key={key} className="flex items-center gap-2">
              <input
                id={`review-setting-${key}`}
                type="checkbox"
                checked={pendingReviewSettings[key] ?? org?.[key] ?? REVIEW_SETTING_DEFAULTS[key]}
                disabled={!org}
                onChange={(e) => setPendingReviewSettings((prev) => ({ ...prev, [key]: e.target.checked }))}
                className="h-4 w-4 rounded border-border-medium text-rio-600 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <label htmlFor={`review-setting-${key}`} className="cursor-pointer text-sm font-medium text-text-primary">
                {label}
              </label>
              <InfoTooltip label={`About ${label}`} text={tooltip} iconClassName="h-3.5 w-3.5 text-text-tertiary" />
            </div>
          ))}
        </div>

        {/* Both fields are absent until the backend that sends them is
            deployed. Hidden rather than shown with fallbacks, which would
            read "Each provider's login" for a data-warehouse org and show a tooltip
            that's wrong for CentralReach. */}
        {org?.ehr_integration_type && org.allowed_ehr_integration_types && (
          <EhrIntegrationTypeField
            value={integrationType}
            savedValue={savedIntegrationType}
            allowed={org.allowed_ehr_integration_types}
            onChange={setPendingIntegrationType}
            disabled={isSaving || isIntegrationTypePending}
            readOnlyReason={canEditIntegrationType ? undefined : NO_PERMISSION_TOOLTIP}
          />
        )}
      </section>

      <Dialog
        // Fall back to the raw value / a generic warning rather than rendering
        // "undefined" if the backend ever sends a type this build doesn't know
        // about yet (e.g. a newly-added enum member before a frontend deploy).
        title={`Switch to ${EHR_INTEGRATION_TYPE_LABELS[integrationType] ?? integrationType}?`}
        open={isConfirmTypeOpen}
        setOpen={setConfirmTypeOpen}
        size="sm"
        primaryButton={{
          text: "Change method",
          onClick: handleConfirmTypeChange,
          disabled: isSaving || isIntegrationTypePending,
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setConfirmTypeOpen(false) }}
      >
        <p className="text-sm text-text-secondary">
          {EHR_INTEGRATION_TYPE_CONFIRM_TEXT[integrationType] ?? "Every active EHR session for this org will be ended."}
        </p>
        {areTogglesDirty && (
          <p className="mt-2 text-sm text-text-secondary">
            Your other unsaved settings will be saved too. Cancel saves nothing.
          </p>
        )}
      </Dialog>

      {org && org.external_id !== undefined && takesVerticalOrgId(org.organization_type) && (
        <VerticalOrgIdSection orgId={orgId} org={org} onSaved={onSaved} />
      )}

      <GoalGenerationRulesSection orgId={orgId} />
    </div>
  );
};
